import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";

import {
  type ErrorHandlerContextType,
  type QueryMetaErrorOptions,
  readQueryMetaErrorOptions,
  useErrorHandler,
} from "@dfp/react-api-core";

type Props = {
  children: ReactNode;
};

export function QueryClientProviderBridge({ children }: Props) {
  const { handleError } = useErrorHandler();

  const handleErrorRef =
    useRef<ErrorHandlerContextType["handleError"]>(handleError);
  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30 * 1000,
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            const meta = readQueryMetaErrorOptions(query.meta);
            handleErrorRef.current(error, {
              message: meta.errorMessage,
              silent: !shouldShowToast(meta),
              context: {
                source: "react-query.query",
                queryKey: query.queryHash,
              },
            });
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _vars, _ctx, mutation) => {
            const meta = readQueryMetaErrorOptions(mutation.meta);
            handleErrorRef.current(error, {
              message: meta.errorMessage,
              silent: !shouldShowToast(meta),
              context: {
                source: "react-query.mutation",
                mutationKey: mutation.options.mutationKey
                  ? safeStringifyKey(mutation.options.mutationKey)
                  : undefined,
              },
            });
          },
        }),
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}

function shouldShowToast(meta: QueryMetaErrorOptions): boolean {
  return meta.showError ?? meta.errorMessage !== undefined;
}

function safeStringifyKey(key: unknown): string {
  try {
    return JSON.stringify(key);
  } catch {
    return String(key);
  }
}
